/**
 * product-enquiry.js
 * -----------------------------------------------------------------------------
 * Custom element for the "Enquiry form" product block (snippets/product-enquiry.liquid).
 *
 * Responsibilities:
 *   1. Keep the hidden Product / Variant / SKU / Link fields in sync with the
 *      shopper's variant selection (listens for Broadcast's `theme:variant:change`).
 *   2. Update the product summary (variant title + image) shown inside the drawer.
 *   3. Fire a "Product Enquiry Submitted" event into Klaviyo on submit, so a
 *      Klaviyo flow can send the customer their confirmation email.
 *   4. Re-open the drawer after a successful submission, so the shopper sees the
 *      confirmation instead of being dumped back at the top of the product page.
 *
 * No theme core files are modified by this script.
 */
(function () {
  'use strict';

  if (customElements.get('product-enquiry')) return;

  /**
   * Klaviyo API revision. Klaviyo pins behaviour to a dated revision and keeps
   * old ones working, so this is safe to leave alone. If you ever need a newer
   * field, bump it to the revision shown in Klaviyo's API docs.
   */
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/events/';

  const selectors = {
    form: '[data-enquiry-form]',
    success: '[data-enquiry-success]',
    fields: '[data-enquiry-fields]',
    conditional: '[data-enquiry-conditional]',
    product: '[data-enquiry-product]',
    variant: '[data-enquiry-variant]',
    sku: '[data-enquiry-sku]',
    link: '[data-enquiry-link]',
    variantTitle: '[data-enquiry-variant-title]',
    image: '[data-enquiry-image] img',
    popupComponent: 'popup-component',
    productComponent: 'product-component',
  };

  class ProductEnquiry extends HTMLElement {
    constructor() {
      super();

      this.popupComponent = this.closest(selectors.popupComponent);
      this.productComponent = this.closest(selectors.productComponent);
      this.dialog = this.closest('dialog');

      this.form = this.querySelector(selectors.form);
      this.success = this.querySelector(selectors.success);
      this.fieldsWrapper = this.querySelector(selectors.fields);
      this.conditional = this.querySelector(selectors.conditional);

      this.fields = {
        product: this.querySelector(selectors.product),
        variant: this.querySelector(selectors.variant),
        sku: this.querySelector(selectors.sku),
        link: this.querySelector(selectors.link),
      };

      this.variantTitle = this.querySelector(selectors.variantTitle);
      this.image = this.querySelector(selectors.image);

      this.hasSubmitted = false;
      this.currentVariant = null;
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onClose = this.resetForm.bind(this);
    }

    connectedCallback() {
      document.addEventListener('theme:variant:change', this.onVariantChange);
      this.form?.addEventListener('submit', this.onSubmit);

      // Broadcast fires this on the <dialog> when the drawer finishes closing.
      this.dialog?.addEventListener('theme:popup:onclose', this.onClose);
      this.dialog?.addEventListener('close', this.onClose);

      if (this.success) {
        this.reopenAfterSubmit();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      this.dialog?.removeEventListener('theme:popup:onclose', this.onClose);
      this.dialog?.removeEventListener('close', this.onClose);
    }

    /* ---------------------------------------------------------------------- */
    /* Variant syncing                                                        */
    /* ---------------------------------------------------------------------- */

    /**
     * Only react to variant changes coming from this product.
     *
     * When rendered as a block we sit inside the product section, so containment
     * is enough. As a standalone section or an inline paste we're outside it, and
     * blanket-accepting every `theme:variant:change` on the page would let a
     * related-products carousel or a quick-add modal overwrite the enquiry's
     * hidden Product / Variant / SKU / Link fields with the wrong item. So fall
     * back to matching on the product handle.
     */
    isOwnEvent(event) {
      const origin = event.target;
      if (!origin || !(origin instanceof Node)) return true;

      if (this.productComponent) {
        return origin === this.productComponent || this.productComponent.contains(origin);
      }

      const handle = this.dataset.productHandle;
      if (!handle || typeof origin.closest !== 'function') return true;

      const source = origin.closest('[data-product-handle]');
      return !source || source.dataset.productHandle === handle;
    }

    handleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !this.isOwnEvent(event)) return;

      // Remember the live variant so resetForm() can re-apply it.
      this.currentVariant = variant;
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      const isDefaultTitle = variant.title === 'Default Title';
      const variantTitle = isDefaultTitle ? '' : variant.title || '';

      this.setValue(this.fields.variant, variantTitle);
      this.setValue(this.fields.sku, variant.sku || '');

      if (this.fields.link) {
        try {
          const url = new URL(this.fields.link.value, window.location.origin);
          url.searchParams.set('variant', variant.id);
          this.fields.link.value = url.toString();
        } catch (error) {
          // Leave the original link in place if the URL can't be parsed.
        }
      }

      if (this.variantTitle) {
        this.variantTitle.textContent = variantTitle;
        this.variantTitle.hidden = variantTitle === '';
      }

      this.updateImage(variant);
      this.updateConditional(variant);
    }

    /**
     * Show or hide the price-conditional field for the selected variant.
     *
     * Toggling `disabled` alongside `hidden` is the important part: a disabled
     * control is skipped by HTML validation and excluded from the submission, so
     * a hidden-but-required field can neither block submit with an error the
     * shopper can't see nor post an empty value.
     */
    updateConditional(variant) {
      if (!this.conditional) return;

      const threshold = parseInt(this.conditional.dataset.conditionalThreshold, 10);
      if (!Number.isFinite(threshold)) return;

      // variant.price is in cents, matching the threshold Liquid wrote out.
      const price = typeof variant.price === 'number' ? variant.price : parseInt(variant.price, 10);
      if (!Number.isFinite(price)) return;

      const show = price >= threshold;

      this.conditional.hidden = !show;
      this.conditional.querySelectorAll('input, textarea, select').forEach((field) => {
        field.disabled = !show;
      });
    }

    updateImage(variant) {
      if (!this.image) return;

      const src = variant.featured_image && variant.featured_image.src;
      if (!src) return;

      const sized = src.replace(/(\.[a-z]{2,4}(\?.*)?)$/i, '_200x$1');
      this.image.setAttribute('src', sized);
      this.image.removeAttribute('srcset');
    }

    setValue(field, value) {
      if (field) field.value = value;
    }

    /* ---------------------------------------------------------------------- */
    /* Klaviyo                                                                */
    /* ---------------------------------------------------------------------- */

    get klaviyoEnabled() {
      return this.dataset.klaviyoEnabled === 'true';
    }

    get klaviyoMetric() {
      return this.dataset.klaviyoMetric || 'Product Enquiry Submitted';
    }

    /**
     * Use the key from the block settings if given, otherwise sniff it off the
     * klaviyo.js tag that Klaviyo's Shopify integration already injects.
     */
    get klaviyoKey() {
      if (this.dataset.klaviyoKey) return this.dataset.klaviyoKey.trim();

      const tag = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
      if (!tag) return null;

      try {
        return new URL(tag.src, window.location.origin).searchParams.get('company_id');
      } catch (error) {
        return null;
      }
    }

    fieldValue(name) {
      const field = this.form?.querySelector(`[name="${name}"]`);
      return field ? field.value.trim() : '';
    }

    buildKlaviyoPayload() {
      const name = this.fieldValue('contact[name]');
      const parts = name.split(/\s+/).filter(Boolean);
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ');

      return {
        data: {
          type: 'event',
          attributes: {
            time: new Date().toISOString(),
            metric: {
              data: {
                type: 'metric',
                attributes: { name: this.klaviyoMetric },
              },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: this.fieldValue('contact[email]'),
                  first_name: firstName,
                  last_name: lastName,
                },
              },
            },
            properties: {
              Product: this.fieldValue('contact[Product]'),
              Variant: this.fieldValue('contact[Variant]'),
              SKU: this.fieldValue('contact[SKU]'),
              Link: this.fieldValue('contact[Link]'),
              Enquiry: this.fieldValue('contact[body]'),
              Name: name,
              Phone: this.fieldValue('contact[phone]'),
              Store: window.location.host,
              Source: 'Product enquiry form',
              ...this.conditionalProperty(),
            },
          },
        },
      };
    }

    /**
     * Carry the conditional field into Klaviyo under its own label, but only
     * when it's actually showing — otherwise every low-value enquiry would
     * carry an empty property.
     */
    conditionalProperty() {
      if (!this.conditional || this.conditional.hidden) return {};

      const name = this.conditional.dataset.conditionalName;
      const field = this.conditional.querySelector('input, textarea, select');
      if (!name || !field || !field.value.trim()) return {};

      return { [name]: field.value.trim() };
    }

    /**
     * `keepalive` lets the request finish even though we navigate away
     * immediately afterwards, so we don't have to stall the form submit.
     */
    sendToKlaviyo() {
      const key = this.klaviyoKey;
      if (!key) return;

      const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(key)}`;

      try {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          mode: 'cors',
          headers: {
            'content-type': 'application/json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(this.buildKlaviyoPayload()),
        }).catch(() => {
          /* Never let a tracking failure block the enquiry. */
        });
      } catch (error) {
        /* Same again — the Shopify submission is what matters. */
      }
    }

    /**
     * Intercept once, fire the Klaviyo event, then let the native Shopify
     * contact form post as normal. The `submit` event only fires after HTML5
     * validation passes, so calling form.submit() here is safe.
     */
    handleSubmit(event) {
      if (this.hasSubmitted) return;

      const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      if (!this.klaviyoEnabled || isDesignMode) return;

      event.preventDefault();
      this.hasSubmitted = true;

      this.sendToKlaviyo();
      this.form.submit();
    }

    /* ---------------------------------------------------------------------- */
    /* Success state                                                          */
    /* ---------------------------------------------------------------------- */

    /**
     * Put the drawer back to a blank form once it's been closed, so a second
     * enquiry doesn't need a page reload. Safe to call when nothing was sent.
     */
    resetForm() {
      if (!this.success || !this.fieldsWrapper) return;

      this.success.hidden = true;
      this.fieldsWrapper.hidden = false;
      this.hasSubmitted = false;

      this.form?.reset();

      // form.reset() restores the values Liquid rendered on page load, which is
      // the wrong variant if the shopper has changed it since. Re-apply.
      if (this.currentVariant) {
        this.applyVariant(this.currentVariant);
      }
    }

    /**
     * Shopify redirects back to the product page with `?contact_posted=true`
     * after a successful contact form submission. The success block is only
     * rendered on the form that actually posted, so its presence is our cue.
     *
     * That query param sticks around, though — so without stripping it, every
     * later reload, back-navigation or bookmark hit would re-render the success
     * state and pop the drawer open again.
     */
    reopenAfterSubmit() {
      const open = () => {
        if (!this.dialog) return;

        if (this.popupComponent && typeof this.popupComponent.popupOpen === 'function') {
          this.popupComponent.popupOpen();
        } else {
          this.dialog.removeAttribute('inert');
          this.dialog.setAttribute('aria-hidden', 'false');

          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
          } else {
            this.dialog.setAttribute('open', '');
          }
        }

        this.clearSubmittedFlagFromUrl();
      };

      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('popup-component').then(() => {
          requestAnimationFrame(open);
        });
      } else {
        requestAnimationFrame(open);
      }
    }

    /**
     * Drop `contact_posted` and the form-id hash from the address bar without
     * reloading. Other params (notably ?variant=) are left intact, and we only
     * ever run this when it was *our* form that posted — so we can't swallow
     * the flag the theme's notify-me popup relies on.
     */
    clearSubmittedFlagFromUrl() {
      if (!window.history || typeof window.history.replaceState !== 'function') return;

      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('contact_posted')) return;

        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState({}, document.title, url.toString());
      } catch (error) {
        /* Cosmetic only — never worth throwing over. */
      }
    }
  }

  customElements.define('product-enquiry', ProductEnquiry);
})();
